import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { suppliersApi } from '../api/suppliers';
import { Supplier } from '../types/supplier';
import { normalizePaginatedResponse } from '../lib/pagination';
import { toast } from 'sonner';

interface UseSuppliersParams {
  search?: string;
  page?: number;
  limit?: number;
}

export function useSuppliers(params?: UseSuppliersParams) {
  return useQuery({
    queryKey: ['suppliers', params?.search, params?.page, params?.limit],
    queryFn: async () => {
      const response = await suppliersApi.getAll({
        search: params?.search,
        page: params?.page,
        limit: params?.limit,
      });
      return normalizePaginatedResponse<Supplier>(response);
    },
    staleTime: 1000 * 60 * 2,
    placeholderData: keepPreviousData,
  });
}

export function useSupplier(id: number) {
  return useQuery({
    queryKey: ['suppliers', id],
    queryFn: async (): Promise<Supplier | null> => {
      const response = await suppliersApi.getById(id);
      return response || null;
    },
    enabled: !!id && id > 0,
    staleTime: 1000 * 60 * 2,
  });
}

export function useCreateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => suppliersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success('Proveedor creado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al crear el proveedor');
    },
  });
}

export function useUpdateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      suppliersApi.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['suppliers', id] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success('Proveedor actualizado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al actualizar el proveedor');
    },
  });
}

export function useDeleteSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => suppliersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast.success('Proveedor eliminado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al eliminar el proveedor');
    },
  });
}